import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalContentData, SignupOnboardingSteps } from '../modal-content';
import { WelcomeMessageComponent } from '../welcome-message/welcome-message.component';
import { NewCheckoutDisclaimerComponent } from '../new-checkout-disclaimer/new-checkout-disclaimer.component';

// A brand new account is not being told about a change, so the step is titled for what it is.
const NEW_CHECKOUT_FLOW_TITLE = 'How your order works';

/**
 * The single modal a brand new account gets on its first load (DEV-10703).
 *
 * The welcome message and the new checkout flow disclaimer used to be opened by
 * two unrelated flows and ended up stacked on top of each other. Here they are
 * steps of one modal instead, each one rendered by the same component that
 * serves it standalone, so the copy and the acceptance rules live in one place.
 */
@Component({
  selector: 'app-signup-onboarding',
  imports: [WelcomeMessageComponent, NewCheckoutDisclaimerComponent],
  templateUrl: './signup-onboarding.component.html',
  styleUrl: './signup-onboarding.component.scss'
})
export class SignupOnboardingComponent implements OnInit {

  #activeModal = inject(NgbActiveModal);

  @Input() modalContentData!: ModalContentData;

  steps = signal<SignupOnboardingSteps[]>([]);
  currentIndex = signal(0);
  welcomeTitle = signal('');
  customWelcomeMessage = signal('');

  stepTypes = SignupOnboardingSteps;

  currentStep = computed(() => this.steps()[this.currentIndex()]);
  totalSteps = computed(() => this.steps().length);
  isMultiStep = computed(() => this.totalSteps() > 1);
  canGoBack = computed(() => this.currentIndex() > 0);
  isLastStep = computed(() => this.currentIndex() === this.totalSteps() - 1);

  stepTitle = computed(() =>
    this.currentStep() === SignupOnboardingSteps.NEW_CHECKOUT_FLOW
      ? NEW_CHECKOUT_FLOW_TITLE
      : this.welcomeTitle()
  );

  ngOnInit(): void {
    const onboarding = this.modalContentData?.signupOnboarding;
    this.steps.set(onboarding?.steps?.length ? [...onboarding.steps] : [SignupOnboardingSteps.WELCOME]);
    this.welcomeTitle.set(onboarding?.welcomeTitle ?? '');
    this.customWelcomeMessage.set(onboarding?.customWelcomeMessage ?? '');
  }

  /** Each step reports itself done; the last one closes the modal. */
  onStepCompleted() {
    if (!this.isLastStep()) return this.currentIndex.update((index) => index + 1);
    this.#activeModal.close(true);
  }

  back() {
    this.currentIndex.update((index) => Math.max(0, index - 1));
  }

}
